// The visibility frame (APP_VISIBILITY §3a, P0): ONE object per pass per tick
// that says whether this frame's view is the one the last decisions were made
// for. Every family that decides what to draw reads it instead of re-deriving
// its own still test; a decision is reused while the pose stays inside the
// key's bound and the scene generations are equal — pixels are never reused
// (wind, water, cabins, avatars keep animating).
//
//   key     the view a decision was made for: eye BOX (every eye at the key
//           ± ep — VR: both eyes), the orientation (quaternion), the
//           projection (exact), viewport, layer mask, scene generations.
//           A new key object per change; families cache on it (memo).
//   stable  this frame's pose is inside the key's bound: every eye inside
//           the eye box, the rotation from the key's ≤ er, projection /
//           viewport / layers / gens bit-equal. A pose leaving the bound
//           builds a new key (a "change" frame pays the tests once).
//   exact   the pose and gens are bit-equal to the LAST frame's — the bound
//           0 class: a decision whose result feeds pixels (the ground tree's
//           geomorph, groundtree.js stillDraw) may reuse only on exact.
//   frustum the key camera's side planes DILATED so every pose inside the
//           bound sees a subset of it (planes(): conservative; no near / far
//           plane — the side planes alone bound a forward pyramid, far is
//           130 km). A test against it may hide a unit for the whole key.
//
// Bounds per class (owner ruling D6, OWNER_RULES 2026-10-24): flat 2 mm /
// 0.05° (a resting mouse is exactly still), VR 4 cm / 1.5° over both eyes
// (head jitter while standing), phone 1 cm / 0.5° (gyro noise).
//
// Draw order (P1, §3d): DRAW_ORDER is THE opaque order table — buildings
// first (the largest near occluder, the cheapest shader), the ground next,
// the rest at 0, the ring after the ground (strict Less: the ground wins a
// tie; H1: the far tree's outer nodes), the sky last. groundtree.js /
// buildings.js read it; sky.js keeps its own literal, check-vis asserts all.
// Inside the ground's slot the levels draw finest first (groundOrder: k/100,
// [−10, −9.9)): three sorts equal renderOrders by material id, and every
// node owns its material — one value would draw them in creation order and
// lose the near-first early-z on the ground shader (measured, §9).
//
// Pure (no three import): scripts/check-vis.mjs runs it under node.

export const DRAW_ORDER = Object.freeze({ buildings: -20, ground: -10, volume: 0, rest: 0, ring: 90, sky: 100 });
// a ground-tree node of level k (0 = finest); a level past 9 shares slot 9
export const groundOrder = (k) => DRAW_ORDER.ground + Math.min(k, 9) / 100;

const DEG = Math.PI / 180;
// VR's projection tolerance (quest-cpu, 2026-11-02): three's XR camera
// (WebXRManager setProjectionFromUnion) rebuilds the union projection EVERY
// frame from the ipd measured between the eyes' world positions, so at world
// coordinates of 10^3–10^4 m its bits change every frame (~1e-12 relative) and
// a bit-exact key never held in a headset — the cull re-decided every unit and
// cell every frame (Quest 3: 3.6–4.1 ms of the XR callback, standing still).
// `pt`: a projection whose every element is within pt · max(1, |key's|) of the
// key's keeps the key; the key's planes are then dilated by er + PROJ_ER, which
// covers it: a side-plane normal (rows 0 / 1 / 3 of P) moves by ≤ √3 · pt ·
// max(1, |P|) / |n| ≤ 6.5e-7 rad for |P| ≤ 3 and |n| ≥ 0.8 (the union's ~100°),
// PROJ_ER = 1e-5 rad is that ×15 (|P| ≤ 45: a 2.5° fov). flat / phone: no pt —
// a resting desktop / phone camera's projection is bit-stable (exact compare).
export const PROJ_ER = 1e-5;
export const VIS_BOUNDS = Object.freeze({
  flat: Object.freeze({ ep: 0.002, er: 0.05 * DEG }),
  vr: Object.freeze({ ep: 0.04, er: 1.5 * DEG, pt: 1e-7 }),
  phone: Object.freeze({ ep: 0.01, er: 0.5 * DEG }),
});
// main.js modes → bound class ('mobile' = the phone)
export function boundsFor(mode) {
  return mode === 'vr' ? VIS_BOUNDS.vr : mode === 'mobile' ? VIS_BOUNDS.phone : VIS_BOUNDS.flat;
}

// the angle between two unit quaternions [x, y, z, w] (radians, 0 … π)
export function quatAngle(a, b) {
  const d = Math.abs(a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3]);
  return 2 * Math.acos(Math.min(1, d));
}

// v rotated by the unit quaternion q (three's Vector3.applyQuaternion)
function rot(q, x, y, z) {
  const [qx, qy, qz, qw] = q;
  const tx = 2 * (qy * z - qz * y), ty = 2 * (qz * x - qx * z), tz = 2 * (qx * y - qy * x);
  return [x + qw * tx + qy * tz - qz * ty, y + qw * ty + qz * tx - qx * tz, z + qw * tz + qx * ty - qy * tx];
}

// the camera-space side planes of a projection (column-major 4 × 4, three's
// Matrix4.elements): inward normals through the apex, [nx, ny, nz] unit, and
// the largest angle of a frustum ray to the view axis (−z). A perspective
// projection only (every pass is; the side planes of one pass through the
// apex — a plane that does not throws, never a silent wrong bound).
export function sidePlanes(P) {
  const row = (r) => [P[r], P[4 + r], P[8 + r], P[12 + r]];
  const r0 = row(0), r1 = row(1), r3 = row(3);
  const planes = [];
  for (const [s, r] of [[1, r0], [-1, r0], [1, r1], [-1, r1]]) {
    // Gribb-Hartmann: row3 ± row; the apex is the origin (w term 0 for a
    // perspective projection's side planes)
    const nx = r3[0] + s * r[0], ny = r3[1] + s * r[1], nz = r3[2] + s * r[2];
    const l = Math.hypot(nx, ny, nz);
    if (Math.abs(r3[3] + s * r[3]) > 1e-9 * l) throw new Error('visframe: a side plane off the apex (not a perspective projection)');
    planes.push([nx / l, ny / l, nz / l]);
  }
  // corner rays: the intersections of adjacent side planes
  let cosMin = 1;
  for (const [i, j] of [[0, 2], [0, 3], [1, 2], [1, 3]]) {
    const a = planes[i], b = planes[j];
    let x = a[1] * b[2] - a[2] * b[1], y = a[2] * b[0] - a[0] * b[2], z = a[0] * b[1] - a[1] * b[0];
    if (z > 0) { x = -x; y = -y; z = -z; }
    const c = -z / Math.hypot(x, y, z);
    if (c < cosMin) cosMin = c;
  }
  return { planes, half: Math.acos(Math.max(-1, Math.min(1, cosMin))) };
}

// the key's world-space planes [nx, ny, nz, c] (n·p + c ≥ 0 inside),
// dilated for the bound: for a pose inside it a view direction d differs
// from one of the key's by ≤ er, so n·d ≥ −2 sin(er/2)|d| for every side
// plane n; inside the dilated pyramid |d| ≤ (d·f) / cos(half + er), so
// n' = n + 2 sin(er/2) / cos(half + er) · f holds for every such d (f the
// key's forward); the eye anywhere in the box adds the box's support on n'.
export function dilatedPlanes(key) {
  const { planes, half } = sidePlanes(key.proj);
  const q = key.quat, f = rot(q, 0, 0, -1);
  const th = Math.min(half + key.er, 89 * DEG);
  const k = key.er > 0 ? 2 * Math.sin(key.er / 2) / Math.cos(th) : 0;
  const cx = (key.lo[0] + key.hi[0]) / 2, cy = (key.lo[1] + key.hi[1]) / 2, cz = (key.lo[2] + key.hi[2]) / 2;
  const hx = (key.hi[0] - key.lo[0]) / 2, hy = (key.hi[1] - key.lo[1]) / 2, hz = (key.hi[2] - key.lo[2]) / 2;
  const out = [];
  for (const p of planes) {
    const w = rot(q, p[0], p[1], p[2]);
    let nx = w[0] + k * f[0], ny = w[1] + k * f[1], nz = w[2] + k * f[2];
    const l = Math.hypot(nx, ny, nz);
    nx /= l; ny /= l; nz /= l;
    const sup = hx * Math.abs(nx) + hy * Math.abs(ny) + hz * Math.abs(nz);
    out.push([nx, ny, nz, -(nx * cx + ny * cy + nz * cz) + sup]);
  }
  return out;
}

// a sphere against planes: false only when wholly outside one of them
export function sphereIn(planes, x, y, z, r) {
  for (const p of planes) if (p[0] * x + p[1] * y + p[2] * z + p[3] < -r) return false;
  return true;
}

const eq = (a, b) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
};
// every element within t · max(1, |a[i]|) (the VR projection tolerance)
const near = (a, b, t) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (!(Math.abs(a[i] - b[i]) <= t * Math.max(1, Math.abs(a[i])))) return false;
  return true;
};

export class VisFrame {
  // pass: 'main' | 'monoCam' | 'scopeCam' | 'viewArm'; bound: VIS_BOUNDS.*
  constructor(pass, bound = VIS_BOUNDS.flat) {
    this.pass = pass;
    this.bound = bound;
    this.key = null;          // the current key (a new object per change)
    this.keys = 0;            // keys built (the change frames)
    this.frames = 0;
    this.stable = false;
    this.exact = false;
    this.stableN = 0;         // frames inside the key's bound
    this.exactN = 0;          // frames bit-equal to the last
    this._last = null;        // last frame's pose + gens (exact test)
  }

  // pose: { eyes: [[x, y, z], …] (the frustum apex first, then VR's two
  // eyes; the key's dilated planes assume the apex is in the box), quat: [x, y, z, w]
  // (the pass camera's world orientation), proj: 16 numbers, vp: [w, h],
  // layers: mask }; gens: { name: number } — every generation a decision
  // reads (a missing / extra name is a change). Returns this.
  begin(pose, gens, bound = this.bound) {
    this.frames++;
    const L = this._last;
    this.exact = !!L && bound === this.bound && poseEq(L.pose, pose) && gensEq(L.gens, gens);
    this.bound = bound;
    this.stable = !!this.key && this._inside(pose, gens);
    if (!this.stable) this.key = this._newKey(pose, gens);
    if (this.stable) this.stableN++;
    if (this.exact) this.exactN++;
    // last frame's pose + gens, copied into reused arrays (no allocation on
    // a steady pose)
    if (!L || L.pose.eyes.length !== pose.eyes.length || !gensSame(L.gens, gens)) this._last = { pose: clonePose(pose), gens: { ...gens } };
    else copyPose(pose, L.pose, L.gens, gens);
    return this;
  }

  _inside(pose, gens) {
    const k = this.key, b = this.bound;
    if (k.ep !== b.ep || k.erPose !== b.er || k.pt !== (b.pt || 0)) return false;
    if (!(k.pt ? near(k.proj, pose.proj, k.pt) : eq(k.proj, pose.proj)) || !eq(k.vp, pose.vp) || k.layers !== pose.layers) return false;
    if (!gensEq(k.gens, gens)) return false;
    for (const e of pose.eyes) {
      for (let i = 0; i < 3; i++) if (e[i] < k.lo[i] || e[i] > k.hi[i]) return false;
    }
    return quatAngle(k.quat, pose.quat) <= k.erPose;
  }

  // er: the key's DILATION angle (dilatedPlanes, conservativedepth viewRect) —
  // the pose rotation bound erPose, + PROJ_ER where the bound tolerates
  // projection noise (pt)
  _newKey(pose, gens) {
    const { ep } = this.bound, pt = this.bound.pt || 0, erPose = this.bound.er, er = erPose + (pt ? PROJ_ER : 0);
    const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
    for (const e of pose.eyes) for (let i = 0; i < 3; i++) {
      if (e[i] - ep < lo[i]) lo[i] = e[i] - ep;
      if (e[i] + ep > hi[i]) hi[i] = e[i] + ep;
    }
    this.keys++;
    return {
      id: this.keys, pass: this.pass, ep, er, erPose, pt, lo, hi,
      quat: pose.quat.slice(), proj: Array.from(pose.proj), vp: pose.vp.slice(), layers: pose.layers,
      gens: { ...gens }, memo: new Map(), _planes: null,
    };
  }

  // the key's dilated side planes (lazy, once per key)
  planes() {
    const k = this.key;
    if (!k._planes) k._planes = dilatedPlanes(k);
    return k._planes;
  }

  // a value computed once per key: fn(key) on the change frame, the cached
  // value on every stable one
  memo(name, fn) {
    const m = this.key.memo;
    if (!m.has(name)) m.set(name, fn(this.key));
    return m.get(name);
  }
}

function poseEq(a, b) {
  if (a.eyes.length !== b.eyes.length || a.layers !== b.layers) return false;
  for (let i = 0; i < a.eyes.length; i++) if (!eq(a.eyes[i], b.eyes[i])) return false;
  return eq(a.quat, b.quat) && eq(a.proj, b.proj) && eq(a.vp, b.vp);
}
function gensEq(a, b) {
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  for (const n of ka) if (a[n] !== b[n]) return false;
  return true;
}
// the same gen names (the copy keeps its object)
function gensSame(a, b) {
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  for (const n of ka) if (!(n in b)) return false;
  return true;
}
function copyPose(p, to, toGens, gens) {
  for (let i = 0; i < p.eyes.length; i++) for (let j = 0; j < 3; j++) to.eyes[i][j] = p.eyes[i][j];
  for (let j = 0; j < 4; j++) to.quat[j] = p.quat[j];
  for (let j = 0; j < 16; j++) to.proj[j] = p.proj[j];
  to.vp[0] = p.vp[0]; to.vp[1] = p.vp[1];
  to.layers = p.layers;
  for (const n in gens) toGens[n] = gens[n];
}
function clonePose(p) {
  return { eyes: p.eyes.map((e) => e.slice()), quat: p.quat.slice(), proj: Array.from(p.proj), vp: p.vp.slice(), layers: p.layers };
}

// the pose of a three camera (duck-typed: matrixWorld, projectionMatrix,
// layers.mask) into `out` — no allocation per frame. The camera's own
// position is eyes[0] (the apex of its frustum: VR's union camera sits
// behind both eyes, WebXRManager setProjectionFromUnion); `eyes` adds the
// eye cameras (VR: the XR camera's two) to the box.
export function poseOf(cam, out, vp, eyes = null) {
  out.eyes = out.eyes || [];
  const n = 1 + (eyes ? eyes.length : 0);
  out.eyes.length = n;
  for (let i = 0; i < n; i++) {
    const m = (i ? eyes[i - 1] : cam).matrixWorld.elements, e = out.eyes[i] || (out.eyes[i] = [0, 0, 0]);
    e[0] = m[12]; e[1] = m[13]; e[2] = m[14];
  }
  const q = out.quat || (out.quat = [0, 0, 0, 1]);
  quatOfMatrix(cam.matrixWorld.elements, q);
  out.proj = cam.projectionMatrix.elements;
  out.vp = vp;
  out.layers = cam.layers.mask;
  return out;
}

// the rotation of a world matrix (column-major, unscaled) as [x, y, z, w]
// (three's Quaternion.setFromRotationMatrix)
export function quatOfMatrix(te, q) {
  const m11 = te[0], m12 = te[4], m13 = te[8], m21 = te[1], m22 = te[5], m23 = te[9], m31 = te[2], m32 = te[6], m33 = te[10];
  const tr = m11 + m22 + m33;
  if (tr > 0) {
    const s = 0.5 / Math.sqrt(tr + 1);
    q[3] = 0.25 / s; q[0] = (m32 - m23) * s; q[1] = (m13 - m31) * s; q[2] = (m21 - m12) * s;
  } else if (m11 > m22 && m11 > m33) {
    const s = 2 * Math.sqrt(1 + m11 - m22 - m33);
    q[3] = (m32 - m23) / s; q[0] = 0.25 * s; q[1] = (m12 + m21) / s; q[2] = (m13 + m31) / s;
  } else if (m22 > m33) {
    const s = 2 * Math.sqrt(1 + m22 - m11 - m33);
    q[3] = (m13 - m31) / s; q[0] = (m12 + m21) / s; q[1] = 0.25 * s; q[2] = (m23 + m32) / s;
  } else {
    const s = 2 * Math.sqrt(1 + m33 - m11 - m22);
    q[3] = (m21 - m12) / s; q[0] = (m13 + m31) / s; q[1] = (m23 + m32) / s; q[2] = 0.25 * s;
  }
  return q;
}
